import { useEffect, useState } from 'react';
import api from '../api/api';
import styled from 'styled-components';

const Container = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @media (min-width: 1024px) {
    padding: 6rem 10% 4rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProjectCard = styled.article`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-6px);
    background: rgba(30, 41, 59, 0.65);
    border-color: rgba(99, 102, 241, 0.3);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(99, 102, 241, 0.1);
  }
`;

const HighlightBadge = styled.span`
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  color: #0b0f19;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  display: inline-block;
  margin-bottom: 0.8rem;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 180px;
  overflow: hidden;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProjectTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.75rem;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background: rgba(99, 102, 241, 0.1);
  color: #a5b4fc;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const StatusTag = styled.span`
  background: ${(props) => {
    switch (props.status) {
      case 'Concluído': return 'rgba(16, 185, 129, 0.1)';
      case 'Em andamento': return 'rgba(245, 158, 11, 0.1)';
      case 'Pausado': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'Concluído': return '#34d399';
      case 'Em andamento': return '#fbbf24';
      case 'Pausado': return '#f87171';
      default: return '#9ca3af';
    }
  }};
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #94a3b8;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ExtraDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 1rem;
  margin-top: auto;
`;

const TechList = styled.p`
  color: #94a3b8;
  font-size: 0.85rem;
  
  strong {
    color: #e2e8f0;
  }
`;

const Period = styled.p`
  color: #64748b;
  font-size: 0.8rem;

  strong {
    color: #cbd5e1;
  }
`;

const Responsibilities = styled.p`
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.5;

  strong {
    color: #e2e8f0;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const Button = styled.a`
  flex: 1;
  text-align: center;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.2s ease;
`;

const DemoButton = styled(Button)`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }
`;

const CodeButton = styled(Button)`
  background: transparent;
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const Empty = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  padding: 4rem 2rem;
  background: rgba(30, 41, 59, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

function Projetos() {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    async function dadosProjetos() {
      try {
        const resposta = await api.get('/api/projetos');
        setDados(resposta.data || []);
        setErro(null);
      } catch (err) {
        console.error('Erro ao carregar projetos:', err);
        setErro('Não foi possível carregar os projetos');
      } finally {
        setLoading(false);
      }
    }
    dadosProjetos();
  }, []);

  if (loading) {
    return (
      <Container>
        <Title>Projetos</Title>
        <Empty>Carregando...</Empty>
      </Container>
    );
  }

  if (erro) {
    return (
      <Container>
        <Title>Projetos</Title>
        <Empty style={{ color: '#ef4444' }}>{erro}</Empty>
      </Container>
    );
  }

  if (dados.length === 0) {
    return (
      <Container>
        <Title>Projetos</Title>
        <Empty>Nenhum projeto cadastrado ainda.</Empty>
      </Container>
    );
  }

  return (
    <Container id="projetos">
      <Title>Projetos Realizados</Title>

      <Grid>
        {dados.map((item) => (
          <ProjectCard key={item.id}>
            {/* ⭐ Badge de destaque */}
            {item.destaque && (
              <HighlightBadge>
                ⭐ Destaque
              </HighlightBadge>
            )}

            {/* 🖼️ Imagem de capa */}
            {item.imagem_capa && (
              <ImageContainer>
                <Image src={item.imagem_capa} alt={item.titulo} />
              </ImageContainer>
            )}

            {/* 📝 Título */}
            <ProjectTitle>{item.titulo}</ProjectTitle>

            {/* 🏷️ Tags (categoria + status) */}
            <Tags>
              {item.categoria && <Tag>{item.categoria}</Tag>}
              {item.status && <StatusTag status={item.status}>{item.status}</StatusTag>}
            </Tags>

            {/* 📄 Descrição */}
            {item.descricao && (
              <Description>{item.descricao}</Description>
            )}

            <ExtraDetails>
              {/* 🛠️ Tecnologias */}
              {item.tecnologias && (
                <TechList>
                  <strong>🛠️ Techs:</strong> {item.tecnologias}
                </TechList>
              )}

              {/* 📅 Datas */}
              {(item.data_inicio || item.data_fim) && (
                <Period>
                  <strong>📅 Período:</strong> {item.data_inicio || '?'} — {item.data_fim || 'Atual'}
                </Period>
              )}

              {/* 📋 Responsabilidades */}
              {item.responsabilidades && (
                <Responsibilities>
                  <strong>📋 Atividades:</strong> {item.responsabilidades}
                </Responsibilities>
              )}
            </ExtraDetails>

            {/* 🔗 Botões de ação */}
            <Buttons>
              {item.link_demo && (
                <DemoButton 
                  href={item.link_demo} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  🌐 Demo
                </DemoButton>
              )}

              {item.linkrepositorio && (
                <CodeButton 
                  href={item.linkrepositorio} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  💻 Código
                </CodeButton>
              )}
            </Buttons>
          </ProjectCard>
        ))}
      </Grid>
    </Container>
  );
}

export default Projetos;
